/**
 * Field formatters and display labels for the Asset Sales/Purchases
 * change-history feed.
 *
 * Used by: `TransactionDetailPanel.tsx` -> `<ChangeHistory />`.
 */

const fmtCurrency = (v: number): string =>
  v.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  });

const fmtDate = (raw: string): string => {
  try {
    return new Date(`${raw}T00:00:00`).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return raw;
  }
};

const DIRECTION_LABELS: Record<string, string> = {
  sale: "Sale (out)",
  purchase: "Purchase (in)",
  disposal: "Disposal",
};

const TRANSACTION_CATEGORY_LABELS: Record<string, string> = {
  pre_appointment: "Pre-appointment",
  post_appointment: "Post-appointment",
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  paid: "Paid",
  unpaid: "Unpaid",
  partial: "Partial",
  unknown: "Unknown",
};

const AGREEMENT_STATUS_LABELS: Record<string, string> = {
  Formal_Contract: "Formal contract",
  Invoice_Only: "Invoice only",
  No_Documentation: "No documentation",
};

const REVIEW_STATUS_LABELS: Record<string, string> = {
  pending_review: "Pending review",
  reviewed: "Reviewed",
  flagged: "Flagged",
};

export const ASSET_TRANSACTION_FIELD_FORMATTERS: Record<
  string,
  (value: unknown) => string
> = {
  counterpartyName: (v) => String(v ?? "\u2014"),
  assetDescription: (v) => String(v ?? "\u2014"),
  assetClass: (v) => String(v ?? "\u2014"),
  assetIdentifier: (v) => String(v ?? "\u2014"),
  notes: (v) => String(v ?? "\u2014"),
  direction: (v) => DIRECTION_LABELS[String(v)] ?? String(v ?? "\u2014"),
  transactionCategory: (v) =>
    TRANSACTION_CATEGORY_LABELS[String(v)] ?? String(v ?? "\u2014"),
  paymentStatus: (v) =>
    PAYMENT_STATUS_LABELS[String(v)] ?? String(v ?? "\u2014"),
  agreementStatus: (v) =>
    AGREEMENT_STATUS_LABELS[String(v)] ?? String(v ?? "\u2014"),
  reviewStatus: (v) => REVIEW_STATUS_LABELS[String(v)] ?? String(v ?? "\u2014"),
  amountNet: (v) =>
    typeof v === "number" ? fmtCurrency(v) : String(v ?? "\u2014"),
  amountGross: (v) =>
    typeof v === "number" ? fmtCurrency(v) : String(v ?? "\u2014"),
  amountGst: (v) =>
    typeof v === "number" ? fmtCurrency(v) : String(v ?? "\u2014"),
  bookValue: (v) =>
    typeof v === "number" ? fmtCurrency(v) : String(v ?? "\u2014"),
  transactionDate: (v) => (typeof v === "string" && v ? fmtDate(v) : "\u2014"),
  confidence: (v) =>
    typeof v === "number" ? `${Math.round(v * 100)}%` : String(v ?? "\u2014"),
};

export const ASSET_TRANSACTION_FIELD_LABELS: Record<string, string> = {
  counterpartyName: "Counterparty",
  direction: "Direction",
  transactionCategory: "Phase",
  assetClass: "Asset class",
  assetDescription: "Asset description",
  assetIdentifier: "Asset identifier",
  amountNet: "Amount (net)",
  amountGross: "Amount (gross)",
  amountGst: "GST",
  bookValue: "Book value",
  transactionDate: "Transaction date",
  paymentStatus: "Payment status",
  agreementStatus: "Agreement status",
  reviewStatus: "Review status",
  confidence: "Confidence",
  notes: "Notes",
};
